import { Injectable, Injector, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Dayjs } from 'dayjs';
import { Observable, of } from 'rxjs';
import { TReducerState } from '../../../app.config';
import { FilterCardManager } from '../../../shared/components/filter-card/filter-card-manager';
import { TFilterCardFieldConfig, TFilterCardValue } from '../../../shared/components/filter-card/filter-card.interface';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../shared/components/mat-dropdown/mat-dropdown.interface';
import { TItemsComponentPageConfiguration } from '../../../state/user/user.interface';
import {
  DateUtilitiesService,
  TDatePickerRangeOption,
  dateRangeOptionValues,
} from '../../../utilities/date-utilities.service';
import { DropdownUtilitiesService } from '../../../utilities/dropdown-utilities.service';
import { EItemType } from '../../settings/fields-field-sets/field-sets/field-sets.interface';
import {
  TCustomDateRangeConfigurationValue,
  TItemType,
  TItemsFilterCardFieldName,
  TItemsFilterCardFields,
} from './items-filter-card.interface';
import { ItemsFilterCardStore } from './items-filter-card.store';

@Injectable()
export class ItemsFilterCardResolver {
  private readonly store = inject(ItemsFilterCardStore);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly globalStore: Store<TReducerState> = inject(Store);

  public resolve(route: ActivatedRouteSnapshot): Observable<FilterCardManager<TItemsFilterCardFieldName>> {
    const pageConfiguration: TItemsComponentPageConfiguration[] =
      this.globalStore.selectSignal((state: TReducerState) => state.user.user)()?.pageConfiguration?.ItemsComponent ??
      [];
    const setAsDefaultValues: TItemsFilterCardFields[] = (pageConfiguration.find(
      (configuration: TItemsComponentPageConfiguration) => configuration.name === 'filterCard',
    )?.value ?? []) as TItemsFilterCardFields[];

    return of(
      new FilterCardManager<TItemsFilterCardFieldName>(
        this.buildFields(),
        this.buildInitialValue(route, setAsDefaultValues),
      ),
    );
  }

  private buildFields(): TFilterCardFieldConfig<TItemsFilterCardFieldName>[] {
    this.store.getItemCategoryData();
    this.store.getWorkflowStepData();
    this.searchBoards();
    this.searchAssignees();
    this.searchReporters();

    return [
      {
        configuration: { showBackwardForward: true, showErrors: false, showRanges: true },
        label: this.translate.instant('field.dateRange'),
        name: 'dateRange',
        type: 'dateRange',
      },
      {
        configuration: this.buildDropdownConfiguration('filterCard.assignee'),
        label: this.translate.instant('filterCard.assignee'),
        loadStatus: this.store.select((state) => state.assigneesLoading),
        name: 'assignee',
        onSearch: (search: string, selectedOptions: IDropdownOption[]) => this.searchAssignees(search, selectedOptions),
        options: toSignal(this.store.assignees$, { initialValue: [], injector: this.injector }),
        type: 'dropdown',
      },
      {
        configuration: this.buildDropdownConfiguration('filterCard.board'),
        label: this.translate.instant('filterCard.board'),
        loadStatus: this.store.select((state) => state.boardsLoading),
        name: 'board',
        onSearch: (search: string, selectedOptions: IDropdownOption[]) => this.searchBoards(search, selectedOptions),
        options: this.store.boards,
        type: 'dropdown',
      },
      {
        configuration: this.buildDropdownConfiguration('field.reporter'),
        label: this.translate.instant('field.reporter'),
        loadStatus: this.store.select((state) => state.reportersLoading),
        name: 'reporter',
        onSearch: (search: string, selectedOptions: IDropdownOption[]) => this.searchReporters(search, selectedOptions),
        options: toSignal(this.store.reporters$, { initialValue: [], injector: this.injector }),
        type: 'dropdown',
      },
      {
        // Static two-option list: resolved synchronously, so no delayed default to reconcile and no
        // search box worth showing.
        configuration: {
          ...this.buildDropdownConfiguration('field.itemType', true),
          enableSearchFilter: false,
          hasDelayedDefaultValue: false,
          singleSelection: false,
        },
        label: this.translate.instant('field.itemType'),
        name: 'itemType',
        options: signal(DropdownUtilitiesService.getItemTypeOptions(this.translate)),
        type: 'dropdown',
      },
      {
        configuration: this.buildDropdownConfiguration('filterCard.workflowStep', true),
        label: this.translate.instant('filterCard.workflowStep'),
        loadStatus: this.store.select((state) => state.workflowStepsLoading),
        name: 'workflowStep',
        options: this.store.workflowSteps,
        type: 'dropdown',
      },
      {
        configuration: this.buildDropdownConfiguration('filterCard.itemCategory', true),
        label: this.translate.instant('filterCard.itemCategory'),
        loadStatus: this.store.select((state) => state.itemCategoriesLoading),
        name: 'itemCategory',
        options: this.store.itemCategories,
        type: 'dropdown',
      },
    ];
  }

  private buildDropdownConfiguration(labelKey: string, isClientSide = false): IDropdownSettings {
    return {
      hasDelayedDefaultValue: true,
      hasMatError: false,
      isClientSide,
      text: this.translate.instant(labelKey),
    };
  }

  /**
   * Mirrors `ItemsFilterCardService.encodeDateRangeForDefault` (the write side): a saved default is
   * either a named phrase (`'thisMonth'`, re-derived against *today*) or, for a range that didn't
   * match any phrase, a `TCustomDateRangeConfigurationValue` — UTC ISO strings decoded back into the
   * user's own timezone. Nothing saved at all (or a corrupt custom entry missing a date) falls back to
   * `'thisMonth'` — per business decision, the items page always opens with a `dateRange` chip active.
   */
  private resolveDateRangeDefault(
    dateRangeDefault: TCustomDateRangeConfigurationValue | TDatePickerRangeOption | undefined,
  ): [Dayjs, Dayjs] {
    if (!dateRangeDefault) {
      return dateRangeOptionValues.thisMonth();
    }

    if (typeof dateRangeDefault === 'string') {
      return dateRangeOptionValues[dateRangeDefault]();
    }

    const startDate = dateRangeDefault.find((entry) => entry.name === 'startDate')?.value;
    const endDate = dateRangeDefault.find((entry) => entry.name === 'endDate')?.value;

    return startDate && endDate
      ? [
          DateUtilitiesService.convertUTCToUserTimezone(startDate),
          DateUtilitiesService.convertUTCToUserTimezone(endDate),
        ]
      : dateRangeOptionValues.thisMonth();
  }

  /**
   * Everything resolvable before navigation. Pool fields (assignee/board/itemCategory/itemType/
   * reporter/workflowStep) are only set here when a saved default or the URL actually supplies a
   * value — an absent pool field starts with no chip, per the "no filters unless defaulted" rule.
   * `dateRange` is the one exception: it always defaults to `'thisMonth'` unless a saved default
   * overrides it, so the items page always opens with a `dateRange` chip active (kanban/kamishibai
   * also carry their own *local* "this month" fallback for whenever the chip is absent — e.g. the user
   * removes it — that never becomes a real chip; see `ItemsService.getEffectiveDateRange`). `board`'s
   * URL-driven value (from the parallel `ItemsResolver`) lands later via
   * `ItemsFilterCardService.applyRouteContext` → `patchInitialValue`, overriding whatever saved
   * default is set here.
   */
  private buildInitialValue(
    route: ActivatedRouteSnapshot,
    setAsDefaultValues: TItemsFilterCardFields[],
  ): TFilterCardValue<TItemsFilterCardFieldName> {
    const getSetAsDefaultValue = (name: TItemsFilterCardFieldName): TItemsFilterCardFields['value'] | undefined =>
      setAsDefaultValues.find((setAsDefaultValue) => setAsDefaultValue.name === name)?.value;
    const dateRange = this.resolveDateRangeDefault(
      getSetAsDefaultValue('dateRange') as TCustomDateRangeConfigurationValue | TDatePickerRangeOption | undefined,
    );

    return {
      assignee: this.getInitialAssignee(route, getSetAsDefaultValue('assignee') as number[] | undefined),
      board: this.toDelayedOptions(getSetAsDefaultValue('board') as number[] | undefined),
      dateRange: { endDate: dateRange[1], startDate: dateRange[0] },
      itemCategory: this.toDelayedOptions(getSetAsDefaultValue('itemCategory') as number[] | undefined),
      itemType: this.toItemTypeOptions(getSetAsDefaultValue('itemType') as TItemType | undefined),
      reporter: this.toDelayedOptions(getSetAsDefaultValue('reporter') as number[] | undefined),
      workflowStep: this.toDelayedOptions(getSetAsDefaultValue('workflowStep') as number[] | undefined),
    };
  }

  private getInitialAssignee(
    route: ActivatedRouteSnapshot,
    setAsDefaultValue: number[] | undefined,
  ): { id: number; name: string }[] | null {
    const assigneeFromParams: string | null = route.queryParamMap.get('assignee');

    if (assigneeFromParams === 'current_user') {
      return this.toDelayedOptions([this.store.user.id]);
    }

    if (assigneeFromParams !== null && Number.isInteger(Number(assigneeFromParams))) {
      return this.toDelayedOptions([Number(assigneeFromParams)]);
    }

    return this.toDelayedOptions(setAsDefaultValue);
  }

  /** mat-dropdown's `hasDelayedDefaultValue` replaces the placeholder name once the options arrive. */
  private toDelayedOptions(ids: number[] | undefined): { id: number; name: string }[] | null {
    return ids?.length ? ids.map((id) => ({ id, name: '...' })) : null;
  }

  /**
   * A saved default still stores the plain item-type id (`'issue'`) — the format the field used while
   * it was a button toggle — so it has to be decoded into the dropdown's option shape. `'all'` is the
   * old "no type filter" sentinel and now simply means no selection.
   */
  private toItemTypeOptions(itemType: TItemType | undefined): IDropdownOption<EItemType>[] | null {
    if (!itemType || itemType === 'all') {
      return null;
    }

    const options = DropdownUtilitiesService.getItemTypeOptions(this.translate).filter(
      (option: IDropdownOption<EItemType>) => option.id === itemType,
    );

    return options.length ? options : null;
  }

  private searchAssignees(search?: string, selectedOptions: IDropdownOption[] = []): void {
    this.store.getAssigneeData({
      fields: ['name'],
      limit: 500 + (search ? 0 : selectedOptions.length),
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  private searchBoards(search?: string, selectedOptions: IDropdownOption[] = []): void {
    this.store.getBoardData({
      fields: ['name', 'key'],
      limit: 500 + (search ? 0 : selectedOptions.length),
      ...(search ? { search: { searchedFields: ['name', 'key'], searchText: search } } : {}),
    });
  }

  private searchReporters(search?: string, selectedOptions: IDropdownOption[] = []): void {
    this.store.getReporterData({
      fields: ['name'],
      limit: 500 + (search ? 0 : selectedOptions.length),
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }
}
